import { differenceInYears } from 'date-fns';

import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
    CustomerSettingCategory,
    CustomerSettingName,
    CustomerSettingValue,
    MijnKlantinstellingenService
} from '@ap/mijn-klantinstellingen';
import { ApWindowService } from '@ap/cdk';
import { ApDateService } from '@ap/date';
import { KlantbeheerResource, KlantDetails } from '@ap/interface-apic-klantbeheer';
import { Client, Feedback, KlantInvoer, UsabilityOnderzoekResource } from '@ap/interface-apic-usability-onderzoek';

import { MijnFeedbackService } from '../../mijn-feedback.service';
import { FeedbackModalOptions, FeedbackOptions } from '../feedback.model';

/**
 * @module FeedbackModalModule
 * @ngdoc service
 * @name FeedbackModalService
 */
@Injectable({ providedIn: 'root' })
export class FeedbackModalService {
    private readonly klantBeheerResource: KlantbeheerResource = inject(KlantbeheerResource);
    private readonly usabilityOnderzoekResource: UsabilityOnderzoekResource = inject(UsabilityOnderzoekResource);
    private readonly mijnFeedbackService: MijnFeedbackService = inject(MijnFeedbackService);
    private readonly mijnKlantinstellingen: MijnKlantinstellingenService = inject(MijnKlantinstellingenService);
    private readonly windowService: ApWindowService = inject(ApWindowService);

    private static getKlantInvoer(metadata: { [key: string]: string }, birthDate: string): KlantInvoer {
        const age = differenceInYears(new Date(), new Date(birthDate));
        const context = { ...metadata, age: null };

        if (birthDate !== null) {
            if (age <= 17) {
                context.age = '0-17';
            }
            if (age >= 18 && age <= 24) {
                context.age = '18-24';
            }
            if (age >= 25 && age <= 34) {
                context.age = '25-34';
            }
            if (age >= 35 && age <= 44) {
                context.age = '35-44';
            }
            if (age >= 45 && age <= 54) {
                context.age = '45-54';
            }
            if (age >= 55 && age <= 64) {
                context.age = '55-64';
            }
            if (age >= 65) {
                context.age = '65+';
            }
        }
        return { context };
    }

    public postFeedback(feedback: FeedbackOptions, modalOptions: FeedbackModalOptions): Observable<CustomerSettingValue> {
        return this.getIngelogdeKlantDetails(modalOptions.retailMetadata).pipe(
            switchMap((klantDetails: KlantDetails) =>
                this.usabilityOnderzoekResource.creeerFeedback(
                    this.getFeedback(feedback, modalOptions, klantDetails?.geboorte_oprichtings_datum ?? null)
                )
            ),
            switchMap(() => {
                this.mijnFeedbackService.setFeedbackPosted();
                if (modalOptions.lastUsedCategory && modalOptions.lastUsedName) {
                    return this.setLastFeedbackMoment(modalOptions.channelId, modalOptions.lastUsedCategory, modalOptions.lastUsedName);
                }
                return of(null);
            })
        );
    }

    private getIngelogdeKlantDetails(retailMetadata: string): Observable<KlantDetails> {
        return retailMetadata ? of(null) : this.klantBeheerResource.geefIngelogdeKlantDetails();
    }

    private getFeedback(feedback: FeedbackOptions, modalOptions: FeedbackModalOptions, birthDate: string): Feedback {
        return {
            kanaal: {
                id: modalOptions.channelId
            },
            formulier: {
                structuur_versie: 0
            },
            client: this.getClient(),
            gebruikers_invoer: {
                [modalOptions.moodId]: feedback.mood,
                ...(feedback.comments && { [modalOptions.commentsId]: feedback.comments })
            },
            klant_invoer: FeedbackModalService.getKlantInvoer(modalOptions.metadata, birthDate)
        };
    }

    private getClient(): Client {
        return {
            user_agent: this.windowService.getWindow().navigator.userAgent,
            scherm: {
                hoogte: this.windowService.innerHeight(),
                breedte: this.windowService.innerWidth()
            },
            url: this.windowService.location_href() as string
        };
    }

    private setLastFeedbackMoment(
        channelId: string,
        category: CustomerSettingCategory,
        name: CustomerSettingName
    ): Observable<CustomerSettingValue> {
        return this.mijnKlantinstellingen.setCustomerSetting(
            category,
            name,
            `{"channelId":"${channelId}","lastFeedbackMoment":"${ApDateService.utc().toISOString()}"}`
        );
    }
}
